import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useChatStore } from '../../store/chatStore'
import { useAuthStore } from '../../store/authStore'
import { useChatThemeStore } from '../../store/chatThemeStore'
import { useSocket } from '../../hooks/useSocket'
import ChatHeader from './ChatHeader'
import MessageList from './MessageList'
import MessageInput from './MessageInput'
import ChatThemePicker from './ChatThemePicker'
import LoadingSpinner from '../LoadingSpinner'

const ChatWindow = () => {
  const { chatId } = useParams()
  const { user } = useAuthStore()
  const { getThemeClasses } = useChatThemeStore()
  const { 
    currentChat, 
    messages, 
    fetchMessages, 
    fetchChatDetails,
    setCurrentChat, 
    isLoading,
    hasMoreMessages,
    currentPage
  } = useChatStore()
  const { joinChat, leaveChat } = useSocket()
  
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)

  const fetchChatData = useCallback(async (id) => {
    try {
      // Fetch full chat details with participants
      const chatDetails = await fetchChatDetails(id)
      if (chatDetails) {
        setCurrentChat(chatDetails)
      } else {
        // Fallback to basic chat object
        const basicChat = { _id: id }
        setCurrentChat(basicChat)
      }
      
      // Then fetch messages
      await fetchMessages(id, 1)
    } catch (error) {
      console.error('❌ Error fetching chat data:', error)
    }
  }, [fetchChatDetails, setCurrentChat, fetchMessages])

  // Fetch chat data when chatId changes
  useEffect(() => {
    if (chatId && chatId !== currentChat?._id) {
      fetchChatData(chatId)
    }
  }, [chatId, currentChat?._id, fetchChatData])

  // Join/leave socket rooms when chat changes
  useEffect(() => {
    if (currentChat?._id) {
      joinChat(currentChat._id)
      
      return () => {
        leaveChat(currentChat._id)
      }
    }
  }, [currentChat?._id, joinChat, leaveChat])

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && !isLoadingMore) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length, isLoadingMore])

  const loadMoreMessages = async () => {
    if (!hasMoreMessages || isLoadingMore) return
    
    setIsLoadingMore(true)
    try {
      await fetchMessages(currentChat._id, currentPage + 1)
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Handle scroll for loading more messages
  const handleScroll = (e) => {
    const { scrollTop } = e.target
    if (scrollTop === 0 && hasMoreMessages && !isLoadingMore) {
      loadMoreMessages()
    }
  }

  if (!currentChat) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
        <ChatThemePicker />
      </div>
    )
  }

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
        <ChatThemePicker />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-white dark:bg-gray-900 relative">
      {/* Chat Header */}
      <ChatHeader chat={currentChat} />

      {/* Messages Container with Theme Background */}
      <div 
        ref={messagesContainerRef}
        className={`flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin ${getThemeClasses()}`}
        onScroll={handleScroll}
        style={{
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Load More Indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-2">
            <LoadingSpinner size="sm" />
          </div>
        )}

        {/* Messages */}
        <MessageList messages={messages} currentUser={user} />
        
        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <MessageInput chatId={currentChat._id} />

      {/* Theme Picker Modal */}
      <ChatThemePicker />
    </div>
  )
}

export default ChatWindow